import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, Shield, Zap, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl bg-white rounded-xl sm:rounded-2xl shadow-xl h-full max-h-[95vh] sm:max-h-[90vh] flex flex-col lg:flex-row">
        {/* Left Side - Content */}
        <div className="lg:w-1/2 bg-gradient-to-br from-indigo-500 to-purple-600 p-4 sm:p-6 md:p-8 flex flex-col justify-center order-2 lg:order-1">
          <div className="text-center lg:text-left">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-white bg-opacity-20 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto lg:mx-0 mb-3 sm:mb-4 lg:mb-6">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">
              {t('auth.login.title')}
            </h1>
            
            <p className="text-sm sm:text-base lg:text-lg text-white text-opacity-90 mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
              {t('auth.login.subtitle')}
            </p>
            
            <div className="space-y-2 sm:space-y-3 lg:space-y-4">
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Shield className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">{t('home.features.security.title')}</span>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">{t('home.features.setup.title')}</span>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">{t('home.features.interface.title')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="lg:w-1/2 p-4 sm:p-6 md:p-8 flex items-center justify-center order-1 lg:order-2">
          <div className="w-full max-w-xs sm:max-w-sm">
            <div className="text-center mb-4 sm:mb-6 lg:mb-8">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                {t('auth.login.title')}
              </h2>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600">
                {t('auth.login.subtitle')}
              </p>
            </div>
            
            <form className="space-y-3 sm:space-y-4 lg:space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center text-xs sm:text-sm">
                  <div className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white text-xs">!</span>
                  </div>
                  {error}
                </div>
              )}
              
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.login.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.login.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full pl-10 pr-12 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="Votre mot de passe"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium hover:from-indigo-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2 sm:mr-3"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {t('auth.login.submit')}
                    <ArrowRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </div>
                )}
              </button>
              
              <div className="text-center">
                <p className="text-xs sm:text-sm text-gray-600">
                  {t('auth.login.noAccount')}{' '}
                  <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-700 transition-colors">
                    {t('auth.login.register')}
                  </Link>
                </p>
              </div>
            </form>

            <div className="text-center mt-3 sm:mt-4 lg:mt-6">
              <Link 
                to="/" 
                className="inline-flex items-center text-xs sm:text-sm text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                {t('common.back')} {t('navigation.home')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 